export type ProjectZone = {
  label: string
  priority: 'heat' | 'glare' | 'daylight' | 'privacy' | 'night-privacy' | 'security' | 'mixed'
  glazing: 'unknown' | 'single' | 'double' | 'low-e' | 'other'
  notes: string
}

export const MAX_PROJECT_ZONES = 12

export const PROJECT_PRIORITIES: { value: ProjectZone['priority']; label: string }[] = [
  { value: 'mixed', label: 'Mixed / still deciding' },
  { value: 'heat', label: 'Heat control' },
  { value: 'glare', label: 'Glare control' },
  { value: 'daylight', label: 'Preserving daylight' },
  { value: 'privacy', label: 'Visual privacy' },
  { value: 'night-privacy', label: 'Night privacy' },
  { value: 'security', label: 'Security' },
]

export const PROJECT_GLAZING: { value: ProjectZone['glazing']; label: string }[] = [
  { value: 'unknown', label: 'Unknown / needs assessment' },
  { value: 'single', label: 'Single-pane' },
  { value: 'double', label: 'Double-pane' },
  { value: 'low-e', label: 'Low-E (details to verify)' },
  { value: 'other', label: 'Other documented glass' },
]

const reviewQuestions: Record<ProjectZone['priority'], string[]> = {
  heat: ['Which windows receive sun, and at what times does heat affect the room?', 'What daylight and exterior appearance should the sample review preserve?'],
  glare: ['Which screens or work surfaces have glare, and at what times?', 'What view and daylight should remain after treatment?'],
  daylight: ['Where is daylight most important, and how is the room used?', 'How should samples be compared for light, appearance and any heat or glare concerns?'],
  privacy: ['Which sightlines and coverage heights need visual privacy?', 'How do lighting, viewing distance and activity affect the privacy needed?'],
  'night-privacy': ['What must remain private after dark with the interior lights on?', 'What coverage and sample review under evening lighting are needed?'],
  security: ['What security objective should the site assessment address?', 'What glass, frame condition and installation scope need review?'],
  mixed: ['Which priorities matter most in this zone, and when do they occur?', 'What trade-offs between view, daylight, privacy and security need review?'],
}

function plainText(value: string): string {
  return value.replace(/\r\n?/g, '\n').replace(/[\p{Cc}\p{Cf}]/gu, character => character === '\n' ? '\n' : character === '\t' ? ' ' : '').trim()
}

export function projectChecklistSummary(zones: readonly ProjectZone[]): string {
  const scope = zones.map((zone, index) => {
    const label = plainText(zone.label).replace(/\n/g, ' ') || 'Unnamed zone'
    const notes = plainText(zone.notes)
    return [
      `Zone ${index + 1}: ${label}`,
      `Priority: ${PROJECT_PRIORITIES.find(option => option.value === zone.priority)?.label}`,
      `Glazing reported: ${PROJECT_GLAZING.find(option => option.value === zone.glazing)?.label}`,
      'Reported glazing is not verified; confirm the glass construction, coatings and condition.',
      'Your notes:',
      notes ? notes.split('\n').map(line => `  ${line}`).join('\n') : '  No notes supplied.',
      'Questions for review:',
      ...reviewQuestions[zone.priority].map(question => `- ${question}`),
    ].join('\n')
  })
  return [
    'COMMERCIAL WINDOW FILM PROJECT CHECKLIST',
    `${zones.length} ${zones.length === 1 ? 'zone' : 'zones'} recorded. Prepared by the visitor for discussion; not submitted to Clear Guard.`,
    'This checklist does not select a film or approve an installation.',
    'All glass and film compatibility must be assessed on site.',
    'Reflective solar film is not dependable night privacy.',
    'Decorative privacy film is not security film.',
    'No security outcome or energy savings are promised.',
    '',
    scope.join('\n\n'),
    '',
    'PROJECT REVIEW',
    'Gather available glazing records, photos, approximate scope, access restrictions and required timing.',
    'Clear Guard reviews commercial requirements before arranging a site assessment.',
    'Copy or download this checklist before leaving the page. It is not saved or attached to a quote request.',
  ].join('\n')
}

export function projectChecklistQuoteHref(zones: readonly ProjectZone[]): string {
  if (!zones.length) return '/commercial/quote'
  if (zones.every(zone => ['heat', 'glare', 'daylight'].includes(zone.priority))) return '/commercial/quote?service=solar'
  if (zones.every(zone => ['privacy', 'night-privacy'].includes(zone.priority))) return '/commercial/quote?service=privacy'
  if (zones.every(zone => zone.priority === 'security')) return '/commercial/quote?service=security'
  return '/commercial/quote'
}
